import React, { useState } from "react";
import { FormControlLabel, IconButton, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AccountBalanceWallet, Close } from "@mui/icons-material";
import SelectField from "controls/global/select-field";
import FieldError from "controls/global/field-error/FieldError";
import TextInputField from "controls/global/text-input-field";
import DatePickerField from "controls/global/datepicker";
import NumberInputFieldPricing from "controls/global/number-input-field/pricing/NumberInputFieldPricing";
import StewartCheckbox from "controls/global/stewart-checkbox";
import ActionButton from "controls/global/action-button";
import AgencyLocationField from "pages/components/AgencyLocationField";
import { showToast } from "controls/global/stewart-toast";
import { CompanyLocation } from "entities/UIModel/CompanyLocation";
import { addAccountingReceivable } from "utils/api/accountingReceivableApi";
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDescriptionTypography,
  IconContainer,
  CloseIconContainer,
  StyledDialogContent,
  FieldsGrid,
  FullWidthCell,
  FieldCell,
  RequiredFieldCell,
  DialogFooter,
} from "../shared/SharedDialogStyles";
import {
  YEAR_MONTH_FORMAT_ERROR,
  YEAR_MONTH_REGEX,
  commentsField,
  getCommentsError,
  toYearMonthDay,
  toAccountingPeriod,
} from "../shared/dialogHelper";
import useReceiptTypeOptions from "utils/custom-hooks/useReceiptTypeOptions";

interface AddAccountReceivablesProps {
  open: boolean;
  onClose: () => void;
}

export interface AddAccountReceivables {
  agent: string;
  accountPeriod: string;
  transactionDate: Date | null;
  accountRecordType: string;
  receiptNumber: string;
  amount: string;
  primaryNumber: string;
  subNumber: string;
  deptNumber: string;
  bankAccountNo: string;
  isCancel: boolean;
  comments: string;
}

const initialValues: AddAccountReceivables = {
  agent: "",
  accountPeriod: "",
  transactionDate: null,
  accountRecordType: "",
  receiptNumber: "",
  amount: "",
  primaryNumber: "",
  subNumber: "",
  deptNumber: "",
  bankAccountNo: "",
  isCancel: false,
  comments: "",
};

const addAccountReceivablesSchema = yup.object({
  agent: yup.string().required("Agent is required"),
  accountPeriod: yup
    .string()
    .required("Required")
    .matches(YEAR_MONTH_REGEX, { message: YEAR_MONTH_FORMAT_ERROR, excludeEmptyString: true }),
  transactionDate: yup
    .date()
    .nullable()
    .required("Required")
    .typeError("Invalid date"),
  accountRecordType: yup.string().required("Required"),
  receiptNumber: yup.string().required("Required"),
  amount: yup.string().required("Required"),
  primaryNumber: yup.string().required("Required"),
  subNumber: yup.string().required("Required"),
  deptNumber: yup.string(),
  bankAccountNo: yup.string(),
  isCancel: yup.boolean(),
  comments: commentsField(),
});

const AddAccountReceivablesModal = ({
  open,
  onClose,
}: AddAccountReceivablesProps) => {
  const [agentLocation, setAgentLocation] = useState<CompanyLocation>();
  const rcptTypeOptions = useReceiptTypeOptions();
  const methods = useForm<AddAccountReceivables>({
    mode: "onBlur",
    defaultValues: initialValues,
    resolver: yupResolver(addAccountReceivablesSchema) as any,
  });

  const { handleSubmit, register, reset, watch, setValue, formState } = methods;
  const { errors } = formState;

  const commentsError = getCommentsError(
    watch("comments"),
    errors.comments?.message,
  );

  const handleClose = () => {
    setAgentLocation(undefined);
    reset(initialValues);
    onClose();
  };

  const clearAgentLocation = () => {
    setValue("agent", "");
    setAgentLocation(undefined);
  };

  const handleAgentChange = (location: CompanyLocation | string) => {
    if (!location || typeof location === "string") return;
    setValue("agent", location?.locationFullDisplayName || "");
    setAgentLocation(location);
  };

  const handleAgentBlur = (event: React.ChangeEvent<any>) => {
    const newInputValue = event?.target?.value;
    if (
      agentLocation === undefined ||
      !newInputValue ||
      newInputValue.toLowerCase() !==
        agentLocation?.locationFullDisplayName?.toLowerCase()
    ) {
      clearAgentLocation();
    }
  };

  const onSubmit = async (values: AddAccountReceivables) => {

    const saved = await addAccountingReceivable({
      AgentID: agentLocation?.locationLegacyID,
      AgentName: agentLocation?.locationFullDisplayName,
      AccountingPeriod: toAccountingPeriod(values.accountPeriod),
      TransactionYearMonthDay: toYearMonthDay(values.transactionDate as Date),
      ReceiptTypeCode: values.accountRecordType,
      ReceiptNumber: values.receiptNumber,
      ReceiptAmount: Number(values.amount),
      GLPRINumber: values.primaryNumber,
      GLSubNumber: values.subNumber,
      GLDeptNumber: values.deptNumber,
      BankAccountNumber: values.bankAccountNo,
      IsCancel: values.isCancel,
      Comments: values.comments,
    });

    if (!saved) {
      showToast({
        message:
          "Failed to add the Accounting Receivable entry. Please try again.",
        type: "error",
      });
      return;
    }

    showToast({
      message: "Accounting Receivable entry added successfully",
      type: "success",
    });
    handleClose();
  };

  return (
    <StyledDialog open={open}>
      <FormProvider {...methods}>
        <StyledDialogTitle>
          <IconContainer>
            <AccountBalanceWallet />
          </IconContainer>
          <Typography>Add Account Receivables Entry</Typography>
          <CloseIconContainer>
            <IconButton className="iconButton" onClick={handleClose}>
              <Close />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>

        <StyledDialogContent>
          <StyledDescriptionTypography>
            Record a journal entry or write-off against an agent&apos;s
            receivable balance.
          </StyledDescriptionTypography>

          <FieldsGrid>
            <FullWidthCell>
              <RequiredFieldCell>
                <AgencyLocationField
                  label="Agent"
                  name="agent"
                  onChange={handleAgentChange}
                  onBlur={handleAgentBlur}
                  onClear={clearAgentLocation}
                />
                <FieldError message={errors.agent?.message} />
              </RequiredFieldCell>
            </FullWidthCell>

            <RequiredFieldCell>
              <TextInputField
                name="accountPeriod"
                label={"Accounting Period (YYYY/MM)"}
                placeholder="e.g. 2026/07"
              />
              <FieldError message={errors.accountPeriod?.message} />
            </RequiredFieldCell>
            <RequiredFieldCell>
              <DatePickerField
                name="transactionDate"
                label="Transaction Date"
                showError={false}
              />
              <FieldError message={errors.transactionDate?.message} />
            </RequiredFieldCell>

            <RequiredFieldCell>
              <SelectField
                name="accountRecordType"
                label={"Accounting Record Type"}
                options={rcptTypeOptions}
                optionsTextField="label"
              />
              <FieldError message={errors.accountRecordType?.message} />
            </RequiredFieldCell>

            <RequiredFieldCell>
              <TextInputField
                name="receiptNumber"
                label={"Receipt Number"}
                placeholder="e.g. CHK-2026-07-1248"
              />
              <FieldError message={errors.receiptNumber?.message} />
            </RequiredFieldCell>

            <RequiredFieldCell>
              <NumberInputFieldPricing
                name="amount"
                label="Amount (negative = credit)"
                decimalScale={2}
                isZeroAllowed={true}
                shouldDefaultToZero={false}
              />
              <FieldError message={errors.amount?.message} />
            </RequiredFieldCell>

            <RequiredFieldCell>
              <TextInputField
                name="primaryNumber"
                label="Primary Number"
                placeholder="GL primary"
              />
              <FieldError message={errors.primaryNumber?.message} />
            </RequiredFieldCell>

            <RequiredFieldCell>
              <TextInputField
                name="subNumber"
                label="Sub Number"
                placeholder="GL sub"
              />
              <FieldError message={errors.subNumber?.message} />
            </RequiredFieldCell>

            <FieldCell>
              <TextInputField
                name="deptNumber"
                label="Department Number"
                placeholder="GL dept"
              />
              <FieldError message={errors.deptNumber?.message} />
            </FieldCell>

            <FieldCell>
              <TextInputField
                name="bankAccountNo"
                label="Bank Account Number"
                placeholder="e.g. ****4821"
              />
              <FieldError message={errors.bankAccountNo?.message} />
            </FieldCell>

            <FullWidthCell>
              <FormControlLabel
                control={<StewartCheckbox {...register("isCancel")} />}
                label="Cancel Flag (marks the entry as cancelled / void)"
              />
            </FullWidthCell>

            <FullWidthCell>
              <FieldCell>
                <TextInputField
                  name="comments"
                  label="Comments"
                  placeholder="Optional context for this Accounting Receivable entry"
                  multiline
                  rows={2}
                />
                <FieldError message={commentsError} />
              </FieldCell>
            </FullWidthCell>
          </FieldsGrid>

          <DialogFooter>
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={handleClose}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
            >
              + Add Accounting Receivable Entry
            </ActionButton>
          </DialogFooter>
        </StyledDialogContent>
      </FormProvider>
    </StyledDialog>
  );
};

export default AddAccountReceivablesModal;
